import React, { memo, useRef, useCallback, useEffect } from 'react';
import { isSameDay, isSameMonth } from 'date-fns';
import { BaseShiftCalendar, ShiftCalendarProvider, ShiftCalendarRef } from '@/components/calendar-core';
import { useShiftManagement, useUnifiedShift, useIsInfiniteScrollMode, useInfiniteScrollShiftDataSafe } from '../context/UnifiedShiftProvider';
import { useActionsPanel } from '@/hooks/scheduling/useActionsPanel';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { supabase as supabaseClient } from '@/integrations/supabase/client';
import EditDayTemplateDialog from '../EditDayTemplateDialog';

interface ShiftCalendarContainerProps {
  className?: string;
}

const ShiftCalendarContainer: React.FC<ShiftCalendarContainerProps> = memo(({ className = '' }) => {
  const {
    currentDisplayMonth,
    selectedDate,
    calendarKey,
    hidePastDays,
    expandAllDays,
    show24Hours,
    showEvents,
    handleDaySelect,
    handleCloseDayPanel,
    handleNextMonth,
    handlePreviousMonth,
    handleSetToday,
    handleMonthSelect,
    handleToggleHidePastDays,
    handleToggleExpandAllDays,
    handleToggleEvents,
    setShow24Hours
  } = useShiftManagement();

  const {
    shifts,
    employees,
    events,
    timeLogs,
    shiftTemplates,
    dayTemplates,
    selectedBusinessDetails,
    displayStartTime,
    displayEndTime,
    businessId,
    countryCode,
    loadingShifts,
    loadingEmployees,
    loadingDisplayHours,
    simpleDisplayHours,
    loadingShiftTemplates,
    loadingDayTemplates,
    refetchShifts,
    handleShiftCreated,
    handleShiftUpdated,
    handleShiftDeleted,
    handleAssignEmployeeToShift,
    handleEditDayTemplate,
    handleDeleteDayTemplate,
    selectedShiftIds,
    selectShift,
    selectAllShifts,
    clearShiftSelection,
    selectedShifts,
    // Infinite scroll state
    scrollMode,
    // Edit dialog state
    isEditDialogOpen,
    editedTemplate,
    editedName,
    editedDescription,
    editedShifts,
    setEditedName,
    setEditedDescription,
    setEditedShifts,
    handleSaveTemplate,
    closeEditDialog
  } = useUnifiedShift();

  // Get opening hours data from business details
  const openingHours = selectedBusinessDetails?.opening_hours || [];

  // Check if we're in infinite scroll mode and get date range
  const isInfiniteScrollMode = useIsInfiniteScrollMode();

  // Get infinite scroll data using the safe hook (returns null if not in provider)
  // This is safe to call unconditionally since it doesn't throw
  const infiniteScrollData = useInfiniteScrollShiftDataSafe();

  // Extract allDays for passing to BaseShiftCalendar
  const infiniteScrollDays = isInfiniteScrollMode ? infiniteScrollData?.allDays : undefined;

  // Use actions panel hook for unified actions panel state
  const {
    isOpen: isActionsPanelOpen,
    mode: unifiedActionsPanelMode,
    toggle: toggleActionsPanel,
    setMode: setUnifiedActionsPanelMode,
    // Backward compatibility properties
    isActionsPanelCollapsed,
    isEditingMode
  } = useActionsPanel(businessId);

  // Get selectedShiftDate and shiftToSelect from global context for cross-component scrolling and selection
  const { selectedShiftDate, setSelectedShiftDate, shiftToSelect, setShiftToSelect } = useDashboardTab();

  const shiftCalendarRef = useRef<ShiftCalendarRef>(null);
  const calendarContainerRef = useRef<HTMLDivElement>(null);

  // Track previous selected date to detect changes
  const prevSelectedShiftDateRef = useRef<Date | null>(null);

  // Track previous panel state to detect explicit close actions
  const prevPanelOpenRef = useRef(isActionsPanelOpen);

  // Track previous mode to detect explicit transitions away from day-actions
  const prevModeRef = useRef(unifiedActionsPanelMode);

  // **FIX**: Track programmatic state changes to prevent race conditions
  const isProgrammaticChangeRef = useRef(false);
  const programmaticTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear selected editing date only when explicitly leaving day-actions mode or closing panel
  useEffect(() => {
    // **FIX**: Skip cleanup during programmatic state changes
    if (isProgrammaticChangeRef.current) {
      return;
    }

    const prevMode = prevModeRef.current;
    prevModeRef.current = unifiedActionsPanelMode;

    // Only clear when panel is closed OR when explicitly transitioning away from day-actions
    const shouldClear = !isActionsPanelOpen ||
                       (prevMode === 'day-actions' && unifiedActionsPanelMode !== 'day-actions');

    // Don't clear if we're just switching between non-day-actions modes (e.g., employees -> shift-templates-editing)
    const isModeSwitchBetweenNonDayActions =
      prevMode !== 'day-actions' && unifiedActionsPanelMode !== 'day-actions';

    if (shouldClear && selectedDate && !isModeSwitchBetweenNonDayActions) {
      handleCloseDayPanel();
    }
  }, [isActionsPanelOpen, unifiedActionsPanelMode, handleCloseDayPanel, selectedDate]);

  // Clear shift selection only on explicit panel close (prevents race condition)
  useEffect(() => {
    // **FIX**: Skip cleanup during programmatic state changes
    if (isProgrammaticChangeRef.current) {
      return;
    }

    const wasPanelOpen = prevPanelOpenRef.current;
    prevPanelOpenRef.current = isActionsPanelOpen;

    // Only clear when panel was open and is now closed (explicit close action)
    // This prevents race conditions when panel opens and shift is selected simultaneously
    if (wasPanelOpen && !isActionsPanelOpen) {
      clearShiftSelection();
    }
  }, [isActionsPanelOpen, clearShiftSelection]);

  // **FIX**: Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (programmaticTimeoutRef.current) {
        clearTimeout(programmaticTimeoutRef.current);
        programmaticTimeoutRef.current = null;
      }
    };
  }, []);

  // Helper function to set programmatic change flag with timeout
  // MUST be defined before useEffects that use it in their dependency arrays
  const setProgrammaticChangeFlag = useCallback(() => {
    // Clear any existing timeout
    if (programmaticTimeoutRef.current) {
      clearTimeout(programmaticTimeoutRef.current);
    }

    // Set the flag to prevent useEffect cleanup
    isProgrammaticChangeRef.current = true;

    // Clear the flag after state changes have time to settle
    programmaticTimeoutRef.current = setTimeout(() => {
      isProgrammaticChangeRef.current = false;
    }, 150); // 150ms should be enough for React state updates to complete
  }, []);

  // Scroll to selectedShiftDate when it changes (e.g., from ScheduleOverviewCard day click or AI chat)
  // Also handles selecting a shift for editing when shiftToSelect is set
  useEffect(() => {
    if (!selectedShiftDate) {
      prevSelectedShiftDateRef.current = null;
      return;
    }

    // Check if this is a new date selection (not the same as before)
    const prevDate = prevSelectedShiftDateRef.current;
    const isNewSelection = !prevDate || !isSameDay(selectedShiftDate, prevDate);

    if (isNewSelection) {
      // Navigate to the month if needed
      if (!isSameMonth(selectedShiftDate, currentDisplayMonth)) {
        handleMonthSelect(selectedShiftDate);
      }

      // First, scroll the page to bring the calendar container into view
      if (calendarContainerRef.current) {
        calendarContainerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }

      // Then scroll within the calendar to the specific date (after page scroll settles)
      const scrollTimer = setTimeout(() => {
        if (shiftCalendarRef.current) {
          shiftCalendarRef.current.scrollToDate(selectedShiftDate, 'start');
        }
      }, 300); // Slightly longer delay to allow page scroll to complete

      // If there's a shift to select, select it for editing after scroll
      const selectTimer = setTimeout(() => {
        if (shiftToSelect) {
          // Clear any existing selection and select the new shift
          clearShiftSelection();
          selectShift(shiftToSelect, true);

          // Open the actions panel in shift-templates-editing mode
          if (!isActionsPanelOpen) {
            setProgrammaticChangeFlag();
            toggleActionsPanel();
          }
          setUnifiedActionsPanelMode('shift-templates-editing');

          // Clear the shiftToSelect after selecting
          setShiftToSelect(null);
        }
      }, 400);

      // Update the ref
      prevSelectedShiftDateRef.current = selectedShiftDate;

      // Clear the selected date after scrolling to prevent re-triggering
      const clearTimer = setTimeout(() => {
        setSelectedShiftDate(null);
      }, 500);

      return () => {
        clearTimeout(scrollTimer);
        clearTimeout(selectTimer);
        clearTimeout(clearTimer);
      };
    }
  }, [selectedShiftDate, currentDisplayMonth, handleMonthSelect, setSelectedShiftDate, shiftToSelect, setShiftToSelect, selectShift, clearShiftSelection, isActionsPanelOpen, toggleActionsPanel, setUnifiedActionsPanelMode, setProgrammaticChangeFlag]);

  // Handle edit mode toggle - properly sets both isOpen and mode
  // This fixes the View/Edit toggle visual state issue
  const handleToggleEditMode = useCallback(() => {
    if (isEditingMode) {
      // Currently in edit mode - close panel (View mode)
      toggleActionsPanel();
    } else {
      // Currently in view mode - open panel AND set to day-actions mode
      if (!isActionsPanelOpen) {
        toggleActionsPanel(); // Open the panel
      }
      setUnifiedActionsPanelMode('day-actions'); // Set to edit mode
    }
  }, [isEditingMode, isActionsPanelOpen, toggleActionsPanel, setUnifiedActionsPanelMode]);

  // Handle scroll to today
  const handleScrollToToday = useCallback(() => {
    // First, navigate to today's month
    handleMonthSelect(new Date());
    // Then scroll to today in the calendar
    if (shiftCalendarRef.current) {
      shiftCalendarRef.current.scrollToToday();
    }
  }, [handleMonthSelect]);

  // Enhanced day selection
  const handleDaySelectWithScroll = useCallback((date: Date) => {
    // If actions panel is closed, open it and switch to day-actions mode
    if (!isActionsPanelOpen) {
      toggleActionsPanel(); // Open the panel
      setUnifiedActionsPanelMode('day-actions'); // Switch to day-actions mode
      handleDaySelect(date); // Select the day for editing
    }
    // If panel is open but not in day-actions mode, switch to it
    else if (unifiedActionsPanelMode !== 'day-actions') {
      setUnifiedActionsPanelMode('day-actions');
      handleDaySelect(date); // Select the day for editing
    }
    // If panel is open and in day-actions mode
    else {
      // Check if clicking the already selected day - if so, deselect it
      if (selectedDate && isSameDay(date, selectedDate)) {
        handleCloseDayPanel(); // Clear the selection but keep panel open
      } else {
        handleDaySelect(date); // Select the new day for editing
      }
    }
  }, [handleDaySelect, handleCloseDayPanel, selectedDate, isActionsPanelOpen, unifiedActionsPanelMode, toggleActionsPanel, setUnifiedActionsPanelMode]);

  // Enhanced close day panel
  const handleCloseDayPanelWithRefresh = useCallback(() => {
    handleCloseDayPanel();
  }, [handleCloseDayPanel]);

  // Delete shift handler
  const handleDeleteShift = useCallback(async (shiftId: string) => {
    if (!businessId) return;

    try {
      const { error } = await supabaseClient
        .from('shifts')
        .delete()
        .eq('id', shiftId);

      if (error) throw error;

      handleShiftDeleted();
    } catch (error) {
      console.error('Error deleting shift:', error);
    }
  }, [businessId, handleShiftDeleted]);

  // Enhanced shift selection for template editing mode
  const handleShiftSelectWithModeSwitch = useCallback((shiftId: string, shouldSelect?: boolean) => {
    // If actions panel is closed, open it and switch to shift-templates-editing mode
    if (!isActionsPanelOpen) {
      setProgrammaticChangeFlag(); // **FIX**: Prevent race condition
      toggleActionsPanel(); // Open the panel
      setUnifiedActionsPanelMode('shift-templates-editing'); // Switch to template editing mode
      selectShift(shiftId, true); // Select the shift
    }
    // If panel is open but not in shift-templates-editing mode, switch to it
    else if (unifiedActionsPanelMode !== 'shift-templates-editing') {
      setProgrammaticChangeFlag(); // **FIX**: Prevent race condition
      setUnifiedActionsPanelMode('shift-templates-editing');
      selectShift(shiftId, true); // Select the shift
    }
    // If panel is open and in shift-templates-editing mode
    else {
      // Handle toggle behavior: if shift is already selected and shouldSelect is undefined, deselect it
      if (shouldSelect === undefined && selectedShiftIds && selectedShiftIds.has(shiftId)) {
        selectShift(shiftId, false); // Deselect the shift
      } else {
        selectShift(shiftId, shouldSelect); // Select/deselect based on parameter
      }
    }
  }, [isActionsPanelOpen, unifiedActionsPanelMode, selectedShiftIds, toggleActionsPanel, setUnifiedActionsPanelMode, selectShift, setProgrammaticChangeFlag]);

  return (
    <div ref={calendarContainerRef} className={`h-full flex flex-col min-h-0 ${className}`}>

      {/* Edit Day Template Dialog */}
      <EditDayTemplateDialog
        isOpen={isEditDialogOpen}
        onClose={closeEditDialog}
        onSave={handleSaveTemplate}
        templateName={editedName}
        onNameChange={setEditedName}
        shifts={editedShifts}
        onShiftsChange={setEditedShifts}
        businessId={businessId}
        employees={employees}
      />

      {/* Calendar Container */}
      <div className="flex-1 min-h-0">
        <ShiftCalendarProvider>
          <BaseShiftCalendar
            key={calendarKey} // Force re-render when selectedDate changes
            ref={shiftCalendarRef}
            variant="full"
            currentDisplayMonth={currentDisplayMonth}
            shifts={shifts}
            workingHours={[]} // Pass empty array for now, can be configured similarly
            openingHours={openingHours} // Pass actual opening hours for business hours styling
            displayStartTime={displayStartTime} // Pass explicit start time from shift-based calculation
            displayEndTime={displayEndTime} // Pass explicit end time from shift-based calculation
            businessId={businessId} // Pass businessId for creating shifts
            employees={employees} // Pass employees
            countryCode={countryCode} // Pass country code for holiday detection
            onShiftCreated={handleShiftCreated} // Pass callback for when a shift is created
            refetchShifts={refetchShifts} // Pass targeted refetch function for shifts only
            onShiftUpdated={handleShiftUpdated} // Pass callback for when a shift is updated
            onShiftDeleted={handleShiftDeleted} // Pass callback for when a shift is deleted
            hidePastDays={hidePastDays} // Pass the hidePastDays state
            onDaySelect={handleDaySelectWithScroll} // Pass the day selection handler
            expandAllDays={expandAllDays} // Pass the expandAllDays state
            isEditingMode={isEditingMode} // Pass the editing mode state
            fullHeight={true} // Use full viewport height for shift management
            onAssignEmployee={handleAssignEmployeeToShift} // Pass the new assignment function
            events={events} // Pass events data
            showEvents={showEvents} // Pass events visibility state
            onToggleEvents={handleToggleEvents} // Pass events toggle function
            selectedEditingDate={selectedDate} // Pass the currently selected date for visual highlighting
            onPreviousMonth={handlePreviousMonth}
            onNextMonth={handleNextMonth}
            onSetToday={handleScrollToToday}
            onToggleHidePastDays={handleToggleHidePastDays}
            onToggleExpandAllDays={handleToggleExpandAllDays}
            businessName={selectedBusinessDetails?.name}
            onMonthSelect={handleMonthSelect}
            businessCreatedAt={selectedBusinessDetails?.created_at}
            isActionsPanelCollapsed={isActionsPanelCollapsed}
            onToggleActionsPanel={handleToggleEditMode}
            // Time range control props
            show24Hours={show24Hours}
            onToggleShow24Hours={setShow24Hours}
            // Infinite scroll mode props
            scrollMode={scrollMode}
            infiniteScrollDays={infiniteScrollDays}
            isInfiniteScrollMode={isInfiniteScrollMode}
            // Unified Actions Panel props
            unifiedActionsPanelMode={unifiedActionsPanelMode}
            onUnifiedActionsPanelModeChange={setUnifiedActionsPanelMode}
            // Template props
            shiftTemplates={shiftTemplates}
            loadingShiftTemplates={loadingShiftTemplates}
            onEditShiftTemplate={(id) => {
              // Placeholder for future shift template editing functionality
              console.log('Edit shift template:', id);
            }}
            onDeleteShiftTemplate={async (id) => {
              // Placeholder for future shift template deletion functionality
              console.log('Delete shift template:', id);
            }}
            dayTemplates={dayTemplates}
            loadingDayTemplates={loadingDayTemplates}
            onEditDayTemplate={handleEditDayTemplate}
            onDeleteDayTemplate={handleDeleteDayTemplate}
            // Shift selection props
            selectedShiftIds={selectedShiftIds}
            onShiftSelect={handleShiftSelectWithModeSwitch}
            onSelectAllShifts={selectAllShifts}
            onClearShiftSelection={clearShiftSelection}
            onDeleteShift={handleDeleteShift}
            onEditShift={(shiftId) => {
              // Placeholder for future shift editing functionality
              console.log('Edit shift:', shiftId);
            }}
            onCopyShifts={(shiftIds) => {
              // Placeholder for future shift copying functionality
              console.log('Copy shifts:', shiftIds);
            }}
            onSaveShiftsAsTemplate={(shiftIds, name, description) => {
              // Placeholder for future save as template functionality
              console.log('Save shifts as template:', shiftIds, name, description);
            }}
            // Context loading states
            loadingShifts={loadingShifts}
            loadingEmployees={loadingEmployees}
            loadingDisplayHours={loadingDisplayHours}
            smartDisplayHours={simpleDisplayHours}
          />
        </ShiftCalendarProvider>
      </div>
    </div>
  );
});

ShiftCalendarContainer.displayName = 'ShiftCalendarContainer';

export default ShiftCalendarContainer;